import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import NotFoundPage from '../../../NotFoundPage';
import React, { useContext, useEffect, useMemo } from 'react';
import EntityDefinitionsContext from '../../../../contexts/EntityDefinitionsContext';
import useTabs from '../../../EntityDetailsShow/SelectedEntityProvider/useTabs';
import SelectedEntityProvider from '../../../EntityDetailsShow/SelectedEntityProvider';
import MappedUserContentSettingsProviderGate from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/MappedUserContentSettingsProvider';
import entityDetailsClientTabTypeCheckers from '../../../EntityDetailsShow/entityDetailsClientTabTypeChckers';
import { DASHBOARD_MENU_PORTAL_DIV_ID } from '../../../DashboardShow/constants';
import { TABS_TOP_BAR_HEIGHT, Z_INDEX } from '../../../../constants';
import Dashboard from '../../../../components/Dashboard';
import isPerformanceBoard from '../../../../isPerformanceBoard';
import PeriodsProvider, {
  BoardPeriodsProvider,
} from '../../../../contextProviders/BoardPeriodsProvider';
import { BonusDatasetPeriodsProvider } from '../../../../contextProviders/BonusAppProvider';
import { V3SelectedAppContext } from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import useGetDatasetDefinition from '../../../../hooks/useGetDatasetDefinition';
import PerformanceBoards from '../../../../components/PerformanceBoards';
import isDriverPerformanceBoard from '../../../../isDriverPerformanceBoard';
import isGeneralBoard from '../../../../isBoardWithSlideOut';
import Board from '../../../../components/Board';
import isCustomerLaneBoard from '../../../../isCustomerLaneBoard';
import CustomerLaneCommitBoard from '../../../../components/CustomerLaneCommitsBoard';
import Report from '../../../../components/Report';
import ErrorBoundary from '../../../../components/Common/ErrorBoundary';
import DriverDetailsTab from '../../../EntityDetailsShow/DriverDetailsTab';
import Loading from '../../../../components/Loading';
import DriverBonusOverview from '../../../../components/DriverAX/screens/DriverBonusOverview';
import ScorecardShow from '../../../ScorecardShow';

export const ShowTab = ({ tab }: { tab: EntityDetails.ClientTab }) => {
  // A mapped entity view can be launched from inside a bonus app. In that case
  // the shared board period gates suppress per-board periods (they assume an
  // app-level BonusAppProvider exists), which it does not here. So the mapped
  // entity view owns and injects its own period provider per board tab, and
  // forces the board's period selector to render.
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const getDatasetDefinition = useGetDatasetDefinition();
  const isBonusApp =
    !!selectedProductApp && appTypeCheckers.isBonusApp(selectedProductApp);

  let component: JSX.Element | null = null;
  if (tab === undefined) {
    component = null;
  } else if (entityDetailsClientTabTypeCheckers.isDashboardTab(tab)) {
    component = (
      <>
        <div
          id={DASHBOARD_MENU_PORTAL_DIV_ID}
          style={{
            position: 'absolute',
            right: 24,
            top: TABS_TOP_BAR_HEIGHT + 11,
            zIndex: Z_INDEX.WORKSPACE_DASHBOARD_MENU,
          }}
        />
        <Dashboard key={tab.dashboard.id} dashboard={tab.dashboard} />
      </>
    );
  } else if (entityDetailsClientTabTypeCheckers.isBoardTab(tab)) {
    if (isPerformanceBoard(tab.board)) {
      const datasetDefinition = getDatasetDefinition(tab.board.dataType);
      const isBonusDataset =
        !!datasetDefinition && datasetDefinition.datasetType === 'bonus';
      const performanceBoard = (
        <PerformanceBoards.Board
          key={tab.board.id}
          board={tab.board}
          isPeriodSelectorForced={isBonusApp}
        />
      );
      if (isBonusApp && isBonusDataset) {
        component = (
          <BonusDatasetPeriodsProvider dataset={tab.board.dataType}>
            {performanceBoard}
          </BonusDatasetPeriodsProvider>
        );
      } else if (isBonusApp) {
        component = (
          <BoardPeriodsProvider board={tab.board}>
            {performanceBoard}
          </BoardPeriodsProvider>
        );
      } else {
        component = (
          <PeriodsProvider board={tab.board}>
            {performanceBoard}
          </PeriodsProvider>
        );
      }
    }
    if (isDriverPerformanceBoard(tab.board) || isGeneralBoard(tab.board)) {
      const datasetDefinition = getDatasetDefinition(tab.board.dataType);
      const isBonusDataset =
        !!datasetDefinition && datasetDefinition.datasetType === 'bonus';
      const generalBoard = (
        <Board
          key={tab.board.id}
          board={tab.board}
          initialRunTimeFilters={window.emptyArray}
          initialScopeFilters={window.emptyArray}
          isPeriodSelectorForced={isBonusApp}
        />
      );
      component =
        isBonusApp && isBonusDataset ? (
          <BonusDatasetPeriodsProvider dataset={tab.board.dataType}>
            {generalBoard}
          </BonusDatasetPeriodsProvider>
        ) : (
          <PeriodsProvider board={tab.board}>{generalBoard}</PeriodsProvider>
        );
    }
    if (isCustomerLaneBoard(tab.board)) {
      component = (
        <CustomerLaneCommitBoard board={tab.board} isDeletable={false} />
      );
    }
  } else if (entityDetailsClientTabTypeCheckers.isReportTab(tab)) {
    component = <Report key={tab.report.id} report={tab.report} />;
  } else if (
    entityDetailsClientTabTypeCheckers.isDriverBonusProgramReportTab(tab)
  ) {
    component = (
      <ErrorBoundary>
        <DriverBonusOverview program={tab.bonusProgram} />
      </ErrorBoundary>
    );
  } else if (entityDetailsClientTabTypeCheckers.isDriverDetailsTab(tab)) {
    component = (
      <ErrorBoundary>
        <DriverDetailsTab />
      </ErrorBoundary>
    );
  } else if (entityDetailsClientTabTypeCheckers.isScorecardTab(tab)) {
    component = (
      <ErrorBoundary>
        <ScorecardShow scorecardIdProp={tab.scorecard.id} />
      </ErrorBoundary>
    );
  }

  return <>{component}</>;
};

const MappedEntityViewScreen = ({
  entityApp,
  entity,
  tabs,
}: {
  selectedItem: V3.Navigation.MappedEntityView;
  entityApp: EntityDetails.App;
  entity: EntityDetails.Entity;
  tabs: EntityDetails.ClientTab[];
}) => {
  const navigate = useNavigate();
  const { tabTypeId } = useParams<{
    tabTypeId: string;
  }>();
  const selectedTab = useMemo(() => {
    if (!tabTypeId) {
      return undefined;
    }

    return tabs.find((t) => t.typeId === tabTypeId);
  }, [tabTypeId, tabs]);

  useEffect(() => {
    if (selectedTab === undefined && tabs.length > 0) {
      navigate('/');
    }
  }, [navigate, selectedTab, tabs.length]);

  if (!selectedTab) {
    return <NotFoundPage />;
  }

  return (
    <MappedUserContentSettingsProviderGate
      entity={entity}
      entityAppId={entityApp.id}
    >
      <SelectedEntityProvider entityApp={entityApp} entity={entity}>
        <ShowTab tab={selectedTab} />
      </SelectedEntityProvider>
    </MappedUserContentSettingsProviderGate>
  );
};

const RedirectToFirstTab = ({ tabs }: { tabs: EntityDetails.ClientTab[] }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (tabs.length > 0) {
      navigate(tabs[0].typeId);
    }
  }, [navigate, tabs]);

  return <Loading />;
};

export const useEntityScreenParams = (
  selectedItem: V3.Navigation.MappedEntityView | V3.Navigation.EntityApp,
) => {
  const { entityDetailsApps, entityDefinitions } = useContext(
    EntityDefinitionsContext,
  );
  const entityApp = useMemo(() => {
    return entityDetailsApps.find(
      (a) =>
        a.entityId === selectedItem.entityId &&
        a.id === selectedItem.entityAppId,
    );
  }, [entityDetailsApps, selectedItem.entityAppId, selectedItem.entityId]);
  const entity = useMemo(() => {
    return entityDefinitions.find((ed) => ed.id === selectedItem.entityId);
  }, [entityDefinitions, selectedItem.entityId]);
  const { tabs } = useTabs({ app: entityApp });

  return {
    entityApp,
    entity,
    tabs,
  };
};

const Gate = ({
  selectedItem,
}: {
  selectedItem: V3.Navigation.MappedEntityView;
}) => {
  const { entityApp, entity, tabs } = useEntityScreenParams(selectedItem);

  if (!entityApp || !entity) {
    return <NotFoundPage />;
  }

  return (
    <Routes>
      <Route
        path={'/:tabTypeId/*'}
        element={
          <MappedEntityViewScreen
            tabs={tabs}
            selectedItem={selectedItem}
            entityApp={entityApp}
            entity={entity}
          />
        }
      />
      <Route path={'*'} element={<RedirectToFirstTab tabs={tabs} />} />
    </Routes>
  );
};

export default Gate;
